<template>
  <!--
    Modal listing all hotels in a single multi-hotel production (grouped by packageKey).
    Rows sorted by totalPrice ascending. Clicking הזמן on a row closes the modal
    and emits `book` with that hotel's production item, so the parent grid can
    forward to the /product-page URL builder.
  -->
  <b-modal
    :visible="visible"
    :title="title"
    hide-footer
    centered
    scrollable
    size="lg"
    dialog-class="production-package-dialog"
    @change="$emit('close')"
  >
    <div class="package-dialog__list">
      <div
        v-for="item in sortedItems"
        :key="item.ref"
        class="package-dialog__row"
      >
        <div class="package-dialog__row-info">
          <div class="package-dialog__row-hotel">{{ item.hotelName }}</div>
          <div class="package-dialog__row-meta">
            <span>{{ formatDates(item) }}</span>
            <span class="package-dialog__row-dot">·</span>
            <span>{{ item.nights }} לילות</span>
            <span class="package-dialog__row-dot">·</span>
            <span>{{ item.roomName || item.roomCode }}</span>
          </div>
        </div>
        <div class="package-dialog__row-price-col">
          <div class="package-dialog__row-price">₪{{ formatPrice(item.totalPrice) }}</div>
          <button class="package-dialog__row-cta" @click="onBook(item)">הזמן</button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal } from 'bootstrap-vue';

export default {
  name: 'ProductionPackageDialog',
  components: { BModal },
  props: {
    visible: { type: Boolean, default: false },
    // The packageKey (name of the production) — shown in the title.
    packageKey: { type: String, default: '' },
    // All productions sharing this packageKey.
    items: { type: Array, default: () => [] },
  },
  computed: {
    sortedItems() {
      return [...(this.items || [])].sort(
        (a, b) => (Number(a.totalPrice) || 0) - (Number(b.totalPrice) || 0),
      );
    },
    title() {
      const count = (this.items || []).length;
      return `${this.packageKey} · ${count} מלונות`;
    },
  },
  methods: {
    formatPrice(n) {
      return (Number(n) || 0).toLocaleString('he-IL');
    },
    formatDates(item) {
      const parse = (iso) => {
        if (!iso || typeof iso !== 'string') return null;
        const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return m ? { y: m[1], m: m[2], d: m[3] } : null;
      };
      const pad = (s) => String(s).padStart(2, '0');
      const inD = parse(item.checkin);
      const outD = parse(item.checkout);
      if (!inD || !outD) return '';
      return `${pad(inD.d)}/${pad(inD.m)}-${pad(outD.d)}/${pad(outD.m)}`;
    },
    onBook(item) {
      this.$emit('book', item);
      this.$emit('close');
    },
  },
};
</script>

<style>
/* NOT scoped — needs to reach the b-modal that Bootstrap teleports to <body>. */
.production-package-dialog .modal-header {
  border-bottom: 1px solid #e6ebf1;
  padding: 16px 20px;
}
.production-package-dialog .modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a3a5c;
}
.production-package-dialog .modal-body {
  padding: 12px 20px 20px;
}
</style>

<style scoped>
.package-dialog__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.package-dialog__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  background: #fbfcfd;
  transition: border-color 0.15s, background 0.15s;
}
.package-dialog__row:hover {
  border-color: #b8ceea;
  background: #f4f8fd;
}
.package-dialog__row-info {
  flex: 1;
  min-width: 0;
}
.package-dialog__row-hotel {
  font-size: 15px;
  font-weight: 700;
  color: #1a3a5c;
  margin-bottom: 2px;
}
.package-dialog__row-meta {
  font-size: 12px;
  color: #6b7d95;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.package-dialog__row-dot { color: #b8c6da; }

.package-dialog__row-price-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}
.package-dialog__row-price {
  font-size: 18px;
  font-weight: 800;
  color: #1f4c96;
  font-variant-numeric: tabular-nums;
}
.package-dialog__row-cta {
  background: #f3a75a;
  color: #ffffff;
  border: 1px solid #f3a75a;
  border-radius: 10px;
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.package-dialog__row-cta:hover { background: #e08d3f; border-color: #e08d3f; }
</style>
